import { useCallback, useEffect, useMemo, useState } from "react";
import { DisplayMessage } from "../../../../../services/helpers";
import { getInitialFlowSettings } from "../utils/constants";
import { validateFlow } from "../utils/flowValidator";
import { validateFlowSettings } from "../utils/formValidators";
import { buildDiagnosticFlowRequest } from "../utils/requestBuilders";
import { deserializeFlow } from "../utils/flowSerializer";
import { FlowSettingsField } from "../utils/flowSchema";
import {
  createDiagnosticFlow,
  updateDiagnosticFlow,
  uploadDiagnosticFlowImage,
  getDiagnosticFlowById,
} from "../services/diagnosticFlowsApiService";

export function useDiagnosticFlowData({ resources, flowId }) {
  const isEdit = !!flowId;
  const [flowName, setFlowName] = useState("");
  const [flowSettings, setFlowSettings] = useState(getInitialFlowSettings());
  const [nodes, setNodes] = useState(null);
  const [edges, setEdges] = useState(null);
  const [validation, setValidation] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [redirect, setRedirect] = useState(null);
  const [initialSnapshot, setInitialSnapshot] = useState(null);

  useEffect(() => {
    if (!flowId) return undefined;

    let active = true;
    setLoading(true);
    (async () => {
      const detail = await getDiagnosticFlowById(flowId, resources);
      if (!active) return;

      if (detail) {
        const loadedSettings = {
          [FlowSettingsField.INFORMATION]: detail.information,
          [FlowSettingsField.LABOR_CODES]: detail.dealerInfo.laborCodes,
          [FlowSettingsField.OUTLET_IDS]: detail.dealerInfo.outletIds,
          [FlowSettingsField.BRAND_IDS]: detail.dealerInfo.brandIds,
          [FlowSettingsField.IS_DEFAULT_FOR_WARRANTY]: detail.dealerInfo.isDefaultForWarranty,
          [FlowSettingsField.IS_ACTIVE]: detail.isActive,
          [FlowSettingsField.FLOWCHART_IMAGE]: detail.flowchartImage,
        };
        const { nodes: loadedNodes, edges: loadedEdges } = deserializeFlow(
          detail.jsonFlow
        );
        setFlowName(detail.name);
        setFlowSettings(loadedSettings);
        setNodes(loadedNodes);
        setEdges(loadedEdges);
        setInitialSnapshot({
          request: JSON.stringify(
            buildDiagnosticFlowRequest({
              flowName: detail.name,
              flowSettings: loadedSettings,
              nodes: loadedNodes,
              edges: loadedEdges,
            })
          ),
          image: loadedSettings[FlowSettingsField.FLOWCHART_IMAGE],
        });
      }
      setLoading(false);
    })();

    return () => {
      active = false;
    };
  }, [flowId]);

  const handleCanvasChange = useCallback((nextNodes, nextEdges) => {
    setNodes(nextNodes);
    setEdges(nextEdges);
    setValidation(null);
  }, []);

  const handleNameChange = useCallback((name) => {
    setFlowName(name);
    setValidation(null);
  }, []);

  const hasUnsavedChanges = useMemo(() => {
    if (!isEdit) return true;
    if (!initialSnapshot) return false;
    return (
      JSON.stringify(buildDiagnosticFlowRequest({ flowName, flowSettings, nodes, edges })) !==
        initialSnapshot.request ||
      flowSettings[FlowSettingsField.FLOWCHART_IMAGE] !== initialSnapshot.image
    );
  }, [isEdit, initialSnapshot, flowName, flowSettings, nodes, edges]);

  const runValidation = useCallback(() => {
    const result = validateFlow(flowName, nodes || [], edges || [], resources);
    setValidation(result);
    return result;
  }, [flowName, nodes, edges, resources]);

  const save = useCallback(async () => {
    const graph = validateFlow(flowName, nodes || [], edges || [], resources);
    if (!graph.isValid) {
      setValidation(graph);
      return;
    }

    const fieldErrors = validateFlowSettings({
      flowName,
      laborCodes: flowSettings[FlowSettingsField.LABOR_CODES],
      resources,
    });
    if (fieldErrors.length > 0) {
      DisplayMessage(fieldErrors[0], "error");
      return;
    }

    setSaving(true);
    const request = buildDiagnosticFlowRequest({ flowName, flowSettings, nodes, edges });

    let savedId = flowId;
    if (isEdit) {
      const ok = await updateDiagnosticFlow(flowId, request, resources);
      if (!ok) return setSaving(false);
    } else {
      const created = await createDiagnosticFlow(request, resources);
      if (!created) return setSaving(false);
      savedId = created.id ?? created.Id;
    }

    if (flowSettings[FlowSettingsField.FLOWCHART_IMAGE] instanceof File) {
      await uploadDiagnosticFlowImage(
        savedId,
        flowSettings[FlowSettingsField.FLOWCHART_IMAGE],
        resources
      );
    }

    setSaving(false);
    DisplayMessage(
      isEdit
        ? resources["DiagnosticFlowWasUpdatedWithSuccess"]
        : resources["DiagnosticFlowWasCreatedWithSuccess"],
      "success"
    );
    setRedirect({ pathname: "/diagnosticflows", state: { refreshGrid: true } });
  }, [isEdit, flowId, flowName, flowSettings, nodes, edges, resources]);

  return {
    isEdit,
    loading,
    flowName,
    flowSettings,
    nodes,
    edges,
    validation,
    saving,
    redirect,
    hasUnsavedChanges,
    setFlowSettings,
    handleNameChange,
    handleCanvasChange,
    runValidation,
    clearValidation: () => setValidation(null),
    save,
  };
}
